import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { growthApi } from "@/lib/api";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { Lock } from "lucide-react";
import { toast } from "sonner";

function Phase({ label, guidance }) {
  if (!guidance) return null;
  return (
    <div className="pa-card p-5 md:p-6">
      <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: "var(--pa-gold)" }}>{label}</p>
      {guidance.title && (
        <h3 className="pa-serif text-xl mb-3" style={{ color: "var(--pa-text-primary)" }}>{guidance.title}</h3>
      )}
      {guidance.text && (
        <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{guidance.text}</p>
      )}
      {guidance.archetype && (
        <p className="text-xs mt-3" style={{ color: "var(--pa-text-muted)" }}>
          Draws on your <span style={{ color: "var(--pa-gold)" }}>{guidance.archetype}</span> side.
        </p>
      )}
    </div>
  );
}

export default function Field() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reflection, setReflection] = useState("");
  const [reflections, setReflections] = useState([]);
  const [growthAvailable, setGrowthAvailable] = useState(false);

  useEffect(() => {
    growthApi.getFieldGuidance()
      .then((r) => setData(r))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
    growthApi.listReflections()
      .then((r) => { setReflections(r.reflections || []); setGrowthAvailable(true); })
      .catch(() => setGrowthAvailable(false));
  }, []);

  const submit = async () => {
    if (!reflection.trim()) return;
    try {
      await growthApi.addReflection(reflection.trim());
      const r = await growthApi.listReflections();
      setReflections(r.reflections || []);
      setReflection("");
      toast.success("Reflection saved");
    } catch (e) {
      toast.error(e.message || "Could not save reflection");
    }
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="pa-card p-6 text-center" data-testid={IDS.fieldPage}>
        <Lock size={22} color="var(--pa-gold)" className="mx-auto mb-3" />
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>Field Mode locked</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>{error.message}</p>
        <Link to="/unlock" className="pa-btn-outline">Enter Access Code</Link>
      </div>
    );
  }

  const g = data?.guidance || {};
  const hasAnyPhase = g.setup || g.active || g.analysis;

  return (
    <div className="space-y-6" data-testid={IDS.fieldPage}>
      <header>
        <h1 className="pa-serif text-3xl md:text-4xl" style={{ color: "var(--pa-text-primary)" }}>Field Mode</h1>
        <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>
          Personalized guidance for Setup, Active, and Analysis phases of your investigation.
        </p>
      </header>

      <Phase label="Setup" guidance={g.setup} />
      <Phase label="Active" guidance={g.active} />
      <Phase label="Analysis" guidance={g.analysis} />

      {!hasAnyPhase && (
        <div className="pa-card p-6 text-center">
          <p className="text-sm" style={{ color: "var(--pa-text-secondary)" }}>
            Personalized guidance appears once you have a Primary and Secondary archetype.
          </p>
        </div>
      )}

      {growthAvailable && (
        <section className="pa-card p-5 md:p-6">
          <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-gold)" }}>Reflections</h2>
          <p className="text-sm mb-3" style={{ color: "var(--pa-text-secondary)" }}>
            Short notes on what you noticed, what worked, what to adjust next time.
          </p>
          <textarea
            className="pa-input min-h-[100px] mb-3"
            placeholder="What did you notice?"
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            data-testid="field-reflection-input"
          />
          <button className="pa-btn-gold" onClick={submit} data-testid="field-reflection-submit">
            Save reflection
          </button>
          {reflections.length > 0 && (
            <div className="mt-6 space-y-3">
              {reflections.map((r) => (
                <div key={r.reflection_id} className="pa-card p-3" style={{ borderColor: "var(--pa-card-border)" }}>
                  <p className="text-sm" style={{ color: "var(--pa-text-primary)" }}>{r.text}</p>
                  {r.created_at && (
                    <p className="text-[10px] mt-1" style={{ color: "var(--pa-text-muted)" }}>
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
